import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import Button from 'src/components/Button';
import Flex from 'src/components/Flex';
import { useDocumentScan, type DocumentScan } from 'src/queries/useDocumentScans';
import styles from './styles.module.scss';
import LoadingIcon from 'src/components/LoadingIcon';
import ImageViewer from 'src/components/ImageViewer';
import DocumentScanEncounterEditor from 'src/components/DocumentScanEncounterEditor';

function DocumentScan() {
    const { documentScanId } = useParams<{ documentScanId: string }>();
    const history = useHistory();
    const { data: documentScan, isLoading, error, refetch } = useDocumentScan(documentScanId);

    const assets = documentScan?.assets || [];

    interface AssetContent {
        assetId: string;
        content: string | null;
        isLoading: boolean;
        error: string | null;
    }

    const [assetContents, setAssetContents] = useState<Record<string, AssetContent>>({});
    const [showOcrData, setShowOcrData] = useState(false);
    const [ocrWidth, setOcrWidth] = useState(350);
    const [imageWidthPercent, setImageWidthPercent] = useState(50); // percentage of remaining space
    const [imageWidthPx, setImageWidthPx] = useState<number | null>(null); // pixel width when expanded
    const [isResizingOcr, setIsResizingOcr] = useState(false);
    const [isResizingImage, setIsResizingImage] = useState(false);
    const mainContentRef = useRef<HTMLDivElement>(null);
    const imageSectionRef = useRef<HTMLDivElement>(null);
    const resizeStartPos = useRef({ x: 0, ocrWidth: 0, imageWidthPercent: 0, imageWidthPx: 0 });

    useEffect(() => {
        // For OCR data, the content is already provided by the backend in asset.content
        // We only need to set it in state if it's not already there
        const ocrAssets = assets.filter(
            (asset) =>
                asset.assetType === 'ocr_data' &&
                asset.status === 'uploaded' &&
                asset.content !== undefined &&
                asset.content !== null,
        );
        
        ocrAssets.forEach((asset) => {
            setAssetContents((prev) => {
                if (prev[asset.documentScanAssetId]) {
                    return prev; // Already set
                }

                return {
                    ...prev,
                    [asset.documentScanAssetId]: {
                        assetId: asset.documentScanAssetId,
                        content: typeof asset.content === 'string' ? asset.content : JSON.stringify(asset.content),
                        isLoading: false,
                        error: null,
                    },
                };
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets]);

    const AssetContentDisplay = (props: { 
        asset: DocumentScan['assets'][0];
    }) => {
        const { asset } = props;

        if (!asset.viewUrl || asset.status !== 'uploaded') {
            return (
                <div className={styles.emptyAssetsMessage}>
                    {asset.status === 'failed' ? 'Asset failed to upload' : 'Asset not available'}
                </div>
            );
        }

        if (asset.assetType === 'image') {
            return <ImageViewer imageUrl={asset.viewUrl} alt="Document scan" />;
        }

        if (asset.assetType === 'ocr_data') {
            // OCR data is provided directly in asset.content from the backend
            const contentData = assetContents[asset.documentScanAssetId];
            
            if (!contentData) {
                // If content is available directly on the asset, use it
                if (asset.content) {
                    return (
                        <div className={styles.textContent}>
                            <pre className={styles.textPre}>
                                {typeof asset.content === 'string' ? asset.content : JSON.stringify(asset.content, null, 2)}
                            </pre>
                        </div>
                    );
                }
                return <div className={styles.emptyAssetsMessage}>Loading OCR data...</div>;
            }

            if (contentData.isLoading) {
                return (
                    <Flex align="center" justify="center" className={styles.loadingContent}>
                        <LoadingIcon />
                    </Flex>
                );
            }

            if (contentData.error) {
                return <div className={styles.errorMessage}>Error loading OCR data: {contentData.error}</div>;
            }

            return (
                <div className={styles.textContent}>
                    <pre className={styles.textPre}>{contentData.content}</pre>
                </div>
            );
        }

        if (asset.assetType === 'encounter_data') {
            // Content is now returned directly from the API as a parsed Encounter object
            const encounterContent = asset.content;
            
            if (!encounterContent) {
                return <div className={styles.emptyAssetsMessage}>No encounter data available</div>;
            }

            let jsonContent;
            try {
                // Content should already be an object, but handle both cases
                if (typeof encounterContent === 'string') {
                    jsonContent = JSON.stringify(JSON.parse(encounterContent), null, 2);
                } else {
                    jsonContent = JSON.stringify(encounterContent, null, 2);
                }
            } catch (error) {
                jsonContent = typeof encounterContent === 'string' ? encounterContent : JSON.stringify(encounterContent);
            }

            return (
                <div className={styles.textContent}>
                    <pre className={styles.jsonPre}>{jsonContent}</pre>
                </div>
            );
        }

        return <div className={styles.emptyAssetsMessage}>Unknown asset type</div>;
    };

    // Find the image asset
    const imageAsset = assets.find((asset) => asset.assetType === 'image');
    
    // Find the OCR data asset
    const ocrAsset = assets.find(
        (asset) => asset.assetType === 'ocr_data' && asset.status === 'uploaded'
    );
    
    // Use the encounter from the document scan response
    const encounter = documentScan?.encounter;
    
    // Get the encounter data asset for the editor
    const encounterDataAsset = assets.find(
        (asset) => asset.assetType === 'encounter_data' && asset.status === 'uploaded'
    );

    function handleBack() {
        history.goBack();
    }

    const handleOcrResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizingOcr(true);
        const currentWidth = imageSectionRef.current?.offsetWidth || 0;
        resizeStartPos.current = {
            x: e.clientX,
            ocrWidth,
            imageWidthPercent,
            imageWidthPx: currentWidth,
        };
    }, [ocrWidth, imageWidthPercent]);

    const handleImageResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
            e.nativeEvent.stopImmediatePropagation();
            e.nativeEvent.preventDefault();
        }
        setIsResizingImage(true);
        // Get the actual current width
        const currentWidth = imageSectionRef.current?.offsetWidth || 0;
        resizeStartPos.current = {
            x: e.clientX,
            ocrWidth,
            imageWidthPercent,
            imageWidthPx: currentWidth,
        };
        return false;
    }, [ocrWidth, imageWidthPercent, imageWidthPx]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!mainContentRef.current) return;

        if (isResizingOcr) {
            const deltaX = e.clientX - resizeStartPos.current.x;
            const newOcrWidth = Math.max(200, Math.min(600, resizeStartPos.current.ocrWidth + deltaX));
            setOcrWidth(newOcrWidth);
        } else if (isResizingImage) {
            const containerWidth = mainContentRef.current.offsetWidth;
            const gap = 16; // $margin4
            const handleWidth = 6; // resize handle width
            
            // Calculate available width for image + encounter (excluding gaps and handles)
            let availableWidth;
            if (showOcrData) {
                // OCR is visible: container - OCR width - OCR handle - image handle - 3 gaps
                availableWidth = containerWidth - ocrWidth - handleWidth - handleWidth - (gap * 3);
            } else {
                // OCR is hidden: container - image handle - 2 gaps (one before image, one after handle)
                availableWidth = containerWidth - handleWidth - (gap * 2);
            }
            
            // Get current image section width to calculate the change
            if (!imageSectionRef.current) return;
            
            // Use the starting width from when resize began, plus the delta
            const startWidth = resizeStartPos.current.imageWidthPx || imageSectionRef.current.offsetWidth;
            const deltaX = e.clientX - resizeStartPos.current.x;
            const newImageWidth = startWidth + deltaX;
            
            // Calculate percentage for reference
            const newImageWidthPercent = (newImageWidth / availableWidth) * 100;
            const maxPercentWidth = availableWidth * 0.7;
            const minPercentWidth = availableWidth * 0.2;
            
            // Always allow expansion - use pixel width when exceeding 70% or when already using pixels
            if (newImageWidth > maxPercentWidth || imageWidthPx !== null) {
                // Use pixel width to allow expansion beyond viewport
                setImageWidthPx(Math.max(minPercentWidth, newImageWidth));
                // Update percentage for reference (but it won't be used when pixel width is set)
                if (newImageWidthPercent > 70) {
                    setImageWidthPercent(70);
                }
            } else {
                // Use percentage for normal resizing within viewport (20-70%)
                const clampedPercent = Math.max(20, Math.min(70, newImageWidthPercent));
                setImageWidthPercent(clampedPercent);
                setImageWidthPx(null);
            }
        }
    }, [isResizingOcr, isResizingImage, showOcrData, ocrWidth]);

    const handleMouseUp = useCallback(() => {
        setIsResizingOcr(false);
        setIsResizingImage(false);
    }, []);

    useEffect(() => {
        if (isResizingOcr || isResizingImage) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizingOcr, isResizingImage, handleMouseMove, handleMouseUp]);

    if (isLoading) {
        return (
            <PageContainer>
                <NavBar />
                <PageContent>
                    <Flex direction="column" align="center" justify="center" className={styles.loadingContainer}>
                        <LoadingIcon />
                    </Flex>
                </PageContent>
            </PageContainer>
        );
    }

    if (error || !documentScan) {
        return (
            <PageContainer>
                <NavBar />
                <PageContent>
                    <Flex direction="column" className={styles.container}>
                        <div className={styles.errorMessage}>
                            {error ? 'Error loading document scan' : 'Document scan not found'}
                        </div>
                        <Button text="Go Back" onClick={handleBack} />
                    </Flex>
                </PageContent>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <NavBar />
            <PageContent className={styles.pageContentWrapper}>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="column" className={styles.header}>
                        <Flex align="center" className={styles.titleRow}>
                            <div className={styles.backWrap} onClick={handleBack}>
                                <span className={styles.backArrow}>{`< `}</span>
                            </div>
                            <h1 className={styles.pageTitle}>                            {documentScan.encounter?.enctrNo 
                                ? `Encounter: ${documentScan.encounter.enctrNo}` 
                                : `Document Scan ID: ${documentScan.documentScanId}`
                            }</h1>
                            {ocrAsset && (
                                <Button
                                    type="primary"
                                    text={showOcrData ? 'Hide OCR Data' : 'Show OCR Data'}
                                    onClick={() => setShowOcrData(!showOcrData)}
                                    className={styles.ocrToggleButton}
                                />
                            )}
                        </Flex>
                    </Flex>
                    <Flex direction="row" className={styles.mainContent} ref={mainContentRef}>
                        {showOcrData && ocrAsset && (
                            <>
                                <div 
                                    className={styles.ocrSection}
                                    style={{ width: `${ocrWidth}px` }}
                                >
                                    <h3 className={styles.ocrTitle}>OCR Data</h3>
                                    <div className={styles.ocrContent}>
                                        {(() => {
                                            const contentData = assetContents[ocrAsset.documentScanAssetId];
                                            // If content is available directly on the asset, use it
                                            if (!contentData && ocrAsset.content) {
                                                return (
                                                    <pre className={styles.textPre}>
                                                        {typeof ocrAsset.content === 'string' 
                                                            ? ocrAsset.content 
                                                            : JSON.stringify(ocrAsset.content, null, 2)}
                                                    </pre>
                                                );
                                            }
                                            if (!contentData) {
                                                return <div className={styles.emptyAssetsMessage}>Loading OCR data...</div>;
                                            }
                                            if (contentData.isLoading) {
                                                return (
                                                    <Flex align="center" justify="center" className={styles.loadingContent}>
                                                        <LoadingIcon />
                                                    </Flex>
                                                );
                                            }
                                            if (contentData.error) {
                                                return <div className={styles.errorMessage}>Error loading OCR data: {contentData.error}</div>;
                                            }
                                            return (
                                                <pre className={styles.textPre}>{contentData.content}</pre>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div
                                    className={styles.resizeHandle}
                                    onMouseDown={handleOcrResizeStart}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.nativeEvent.stopImmediatePropagation();
                                    }}
                                />
                            </>
                        )}
                        <div 
                            ref={imageSectionRef}
                            className={styles.imageSection}
                            style={{ 
                                width: imageWidthPx !== null && imageWidthPx > 0
                                    ? `${imageWidthPx}px` 
                                    : `${imageWidthPercent}%` 
                            }}
                        >
                            <div 
                                className={styles.imageViewerContainer}
                                onMouseDown={(e) => {
                                    // Prevent ImageViewer from handling mouseDown if it's from resize handle
                                    const target = e.target as HTMLElement;
                                    if (target.closest(`.${styles.resizeHandle}`)) {
                                        e.stopPropagation();
                                    }
                                }}
                            >
                                {imageAsset ? (
                                    <AssetContentDisplay 
                                        key={imageAsset.documentScanAssetId}
                                        asset={imageAsset}
                                    />
                                ) : (
                                    <div className={styles.emptyState}>No image asset found</div>
                                )}
                            </div>
                        </div>
                        <div
                            className={styles.resizeHandle}
                            onMouseDown={handleImageResizeStart}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.nativeEvent) {
                                    e.nativeEvent.stopImmediatePropagation();
                                    e.nativeEvent.preventDefault();
                                }
                            }}
                            onMouseUp={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.nativeEvent) {
                                    e.nativeEvent.stopImmediatePropagation();
                                }
                            }}
                        />
                        <div className={styles.encounterSectionWrapper}>
                            <DocumentScanEncounterEditor
                                encounter={encounter}
                                encounterDataAsset={encounterDataAsset}
                                onSave={async () => {
                                    await refetch();
                                }}
                            />
                        </div>
                    </Flex>
                </Flex>
            </PageContent>
        </PageContainer>
    );
}

export default DocumentScan;
